import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { compressImage, validateImageFile, type ImageInput } from '@/lib/utils/image';
import { getImageUrl } from '@/lib/utils';

export interface ImageItem {
  id: string | number;
  src: string;
  isNew: boolean;
  order: number;
  file?: File;
  originalIndex?: number; // For existing images, track original position
}

interface UseShopImagesEditorOptions {
  maxImages?: number;
  existingImages?: ImageInput[];
  onError?: (error: string) => void;
}

interface UseShopImagesEditorReturn {
  // All images in current order
  images: ImageItem[];
  // Preview data for ShopImages component
  previews: Array<{ src: string; id: string | number; isNew?: boolean }>;
  // New files to upload
  newFiles: File[];
  // State
  isCompressing: boolean;
  isInitialized: boolean;
  // Actions
  addImages: (fileList: FileList) => Promise<void>;
  removeImage: (id: string | number) => void;
  reorderImages: (fromIndex: number, toIndex: number) => void;
  reset: () => void;
  reinitialize: () => void;
  // Change detection
  hasChanges: boolean;
  hasOrderChanged: boolean;
  // For API calls - get the order data to send to server
  getOrderData: () => Array<{
    url: string;
    order: number;
    isFile: boolean;
    name: string;
    id: string;
  }>;
}

export function useShopImagesEditor(
  options: UseShopImagesEditorOptions = {}
): UseShopImagesEditorReturn {
  const { maxImages = 2, existingImages, onError } = options;

  const [images, setImages] = useState<ImageItem[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialImageIds, setInitialImageIds] = useState<Set<string | number>>(new Set());

  // Track new files separately for upload
  const newFilesRef = useRef<Map<string, File>>(new Map());

  const handleError = useCallback((message: string) => {
    if (onError) {
      onError(message);
    } else {
      toast.error(message);
    }
  }, [onError]);

  // Initialize from existing images
  useEffect(() => {
    if (existingImages && !isInitialized) {
      const items: ImageItem[] = [];
      existingImages.forEach((img, index) => {
        const src = getImageUrl(img);
        if (src) {
          items.push({
            id: index,
            src,
            isNew: false,
            order: index,
            originalIndex: index,
          });
        }
      });

      setImages(items);
      setInitialImageIds(new Set(items.map(img => img.id)));
      setIsInitialized(true);
    } else if (!existingImages && !isInitialized) {
      // No existing images (create mode)
      setIsInitialized(true);
    }
  }, [existingImages, isInitialized]);

  // Add new images
  const addImages = useCallback(async (fileList: FileList) => {
    const currentLength = images.length;
    const files = Array.from(fileList);
    const newFiles = files.slice(0, maxImages - currentLength);

    if (newFiles.length === 0) return;

    // Validate files
    for (const file of newFiles) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        handleError(validation.error || 'Invalid image file');
        return;
      }
    }

    setIsCompressing(true);

    try {
      const newItems: ImageItem[] = [];

      for (let i = 0; i < newFiles.length; i++) {
        // compressImage ne touche pas les fichiers déjà sous la limite (3 Mo),
        // sinon les redimensionne et les ré-encode en JPEG via canvas.
        const processedFile = await compressImage(newFiles[i]);

        const preview = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(processedFile);
        });

        const newId = `new-${Date.now()}-${i}`;

        // Store file reference
        newFilesRef.current.set(newId, processedFile);

        newItems.push({
          id: newId,
          src: preview,
          isNew: true,
          order: currentLength + i,
          file: processedFile,
        });
      }

      setImages(prev => {
        const updated = [...prev, ...newItems];
        return updated.map((img, idx) => ({ ...img, order: idx }));
      });
    } catch (error) {
      console.error('Error processing images:', error);
      handleError('Failed to process images');
    } finally {
      setIsCompressing(false);
    }
  }, [images.length, maxImages, handleError]);

  // Remove image
  const removeImage = useCallback((id: string | number) => {
    // Remove from files ref if it's a new image
    if (typeof id === 'string') {
      newFilesRef.current.delete(id);
    }

    setImages(prev => {
      const filtered = prev.filter(img => img.id !== id);
      return filtered.map((img, idx) => ({ ...img, order: idx }));
    });
  }, []);

  // Reorder images
  const reorderImages = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setImages(prev => {
      const newOrder = [...prev];
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      return newOrder.map((img, idx) => ({ ...img, order: idx }));
    });
  }, []);

  // Reset to empty
  const reset = useCallback(() => {
    setImages([]);
    newFilesRef.current.clear();
    setIsInitialized(false);
    setInitialImageIds(new Set());
  }, []);

  // Reinitialize (for after successful save)
  const reinitialize = useCallback(() => {
    newFilesRef.current.clear();
    setIsInitialized(false);
  }, []);

  // Build previews for ShopImages component
  const previews = images.map(img => ({
    src: img.src,
    id: img.id,
    isNew: img.isNew,
  }));

  // Get new files array (in order)
  const newFiles = images
    .filter(img => img.isNew)
    .map(img => newFilesRef.current.get(img.id as string))
    .filter((file): file is File => file !== undefined);

  // Change detection
  const currentExistingIds = images.filter(img => !img.isNew).map(img => img.id);
  const hasRemovals = Array.from(initialImageIds).some(id => !currentExistingIds.includes(id));
  const hasAdditions = images.some(img => img.isNew);

  const hasOrderChanged = (() => {
    const existingImages = images.filter(img => !img.isNew);
    if (existingImages.length !== initialImageIds.size) return true;
    return existingImages.some((img, idx) => img.originalIndex !== idx);
  })();

  const hasChanges = hasRemovals || hasAdditions || hasOrderChanged;

  // Get order data for API
  const getOrderData = useCallback(() => {
    return images.map((img, index) => {
      if (img.isNew) {
        const file = newFilesRef.current.get(img.id as string);
        return {
          url: 'file',
          order: index,
          isFile: true,
          name: file?.name || `image-${index}.jpg`,
          id: String(img.id),
        };
      } else {
        const imageName = img.src.split('/').pop() || `image-${index}.jpg`;
        return {
          url: img.src,
          order: index,
          isFile: false,
          name: imageName,
          id: String(img.id),
        };
      }
    });
  }, [images]);

  return {
    images,
    previews,
    newFiles,
    isCompressing,
    isInitialized,
    addImages,
    removeImage,
    reorderImages,
    reset,
    reinitialize,
    hasChanges,
    hasOrderChanged,
    getOrderData,
  };
}
